/**
 * Type definitions for the Subtitles-Only feature.
 * These types mirror the backend contract from /api/v1/subtitles/*.
 */

import type { MediaType } from '@/components/common/MediaUploader';

// ============================================================================
// Domain types (mirror backend enums)
// ============================================================================

export type SubtitleOutputMode = 'sidecar' | 'soft_embedded' | 'hard_burn';

export type SubtitleFormat = 'srt' | 'vtt' | 'ass';

// ============================================================================
// SubtitleStyle — mirrors backend SubtitleStyle Pydantic model
// ============================================================================

export interface SubtitleStyle {
  font_family: string;
  font_size: number;
  primary_color: string;
  outline_color: string;
  outline_width: number;
  shadow_depth: number;
  position: 'bottom' | 'top' | 'middle';
  margin_v: number;
}

export const DEFAULT_SUBTITLE_STYLE: SubtitleStyle = {
  font_family: 'Arial',
  font_size: 48,
  primary_color: '#FFFFFF',
  outline_color: '#000000',
  outline_width: 2.0,
  shadow_depth: 0.0,
  position: 'bottom',
  margin_v: 40,
};

// ============================================================================
// API request / response shapes
// ============================================================================

/** POST /api/v1/subtitles/upload response */
export interface SubtitleUploadResponse {
  media_url: string;
  media_type: 'audio' | 'video';
}

/** POST /api/v1/subtitles/generate request body */
export interface SubtitleGenerateRequest {
  media_url: string;
  /** Language code or "auto" */
  source_language: string;
  target_languages: string[];
  output_mode: SubtitleOutputMode;
  formats: SubtitleFormat[];
  use_llm_refinement: boolean;
  /** Only affects ASS format */
  use_karaoke: boolean;
  /** Required for hard_burn mode — must be true to acknowledge rendering cost */
  confirm_cost: boolean;
  /** Optional style overrides for hard_burn ASS rendering */
  style?: SubtitleStyle;
}

/** POST /api/v1/subtitles/generate response (202 Accepted) */
export interface SubtitleGenerateResponse {
  job_id: string;
}

/** GET /api/v1/jobs/{job_id} — unified job status shape (see SubtitleJobStoreAdapter) */
export type SubtitleJobStatus = 'pending' | 'processing' | 'completed' | 'error' | 'failed';

export interface SubtitleJobResponse {
  job_id: string;
  status: SubtitleJobStatus;
  progress?: number;
  message?: string;
  error?: string;
  /** Unified job envelope: store-specific fields live here. */
  extra_metadata?: {
    /** Absolute path of the artifact on this machine (local jobs only). */
    output_file_path?: string | null;
  } | null;
}

// ============================================================================
// UI state
// ============================================================================

export type SubtitleStatus =
  | 'idle'
  | 'uploading'
  | 'generating'
  | 'polling'
  | 'completed'
  | 'error'
  | 'cancelled';

export interface SubtitlesState {
  // Media
  mediaFile: File | null;
  mediaType: MediaType | null;
  mediaUrl: string | null;

  // Languages
  /** "auto" or an NLLB language code */
  sourceLanguage: string;
  targetLanguages: string[];

  // Config
  outputMode: SubtitleOutputMode;
  formats: SubtitleFormat[];
  useLlmRefinement: boolean;
  useKaraoke: boolean;

  // Hard burn
  style: SubtitleStyle;
  hardBurnConfirmed: boolean;

  // Progress / results
  uploadProgress: number;
  jobId: string | null;
  jobStatus: SubtitleJobStatus | null;
  jobProgress: number;
  jobMessage: string;
  /**
   * Absolute path of the finished artifact on THIS machine, when the job ran
   * locally. Lets the desktop app reveal the file instead of "downloading" a
   * file the user already has.
   */
  outputFilePath: string | null;

  // Status
  status: SubtitleStatus;
  error: string | null;
}

// ============================================================================
// Reducer actions
// ============================================================================

export type SubtitlesAction =
  | { type: 'SET_MEDIA'; file: File; mediaType: MediaType }
  | { type: 'CLEAR_MEDIA' }
  | { type: 'SET_MEDIA_URL'; url: string; mediaType: 'audio' | 'video' }
  | { type: 'SET_SOURCE_LANGUAGE'; language: string }
  | { type: 'SET_TARGET_LANGUAGES'; languages: string[] }
  | { type: 'SET_OUTPUT_MODE'; mode: SubtitleOutputMode }
  | { type: 'SET_FORMATS'; formats: SubtitleFormat[] }
  | { type: 'SET_LLM_REFINEMENT'; enabled: boolean }
  | { type: 'SET_KARAOKE'; enabled: boolean }
  | { type: 'SET_STYLE'; style: SubtitleStyle }
  | { type: 'SET_HARD_BURN_CONFIRMED'; confirmed: boolean }
  | { type: 'SET_UPLOAD_PROGRESS'; progress: number }
  | { type: 'START_UPLOADING' }
  | { type: 'START_GENERATING'; jobId: string }
  | { type: 'SET_JOB_STATUS'; status: SubtitleJobStatus; progress: number; message: string }
  | { type: 'SET_COMPLETED'; outputFilePath: string | null }
  | { type: 'SET_ERROR'; error: string }
  | { type: 'CANCEL' }
  | { type: 'RESET' };
